import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { withCookies } from "react-cookie"
import { useIntl } from "gatsby-plugin-react-intl"

import { TextButton } from "./Button"
import { media } from "../styles"

import CloseSVG from "../images/icons/cross--small.inline.svg"

import { daysFromNow } from "../helpers"

const debug = process.env.GATSBY_DEBUG_TRACKING === "true"

const Flyout = styled.aside`
  background: var(--color-greyLight);
  bottom: 0;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.08);
  color: var(--color-spaceblue);
  left: 0;
  max-width: 100%;
  position: fixed;
  right: 0;
  transform: translate3d(0, 100%, 0);
  transition: transform var(--transitionTime);
  width: 100%;
  z-index: 2;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  @media ${media.sm} {
    left: auto;
    max-width: calc(100% - 40px);
    right: 20px;
    transform: translate3d(0, 100%, 0);
    width: ${(props) => `${props.$width || 570}px`};
  }

  ${(props) =>
    props.$isOpen &&
    css`
      && {
        transform: translate3d(0, 0, 0);
      }
    `};
`

const Close = styled(TextButton)`
  align-items: center;
  color: var(--color-sage);
  display: flex;
  height: 24px;
  justify-content: center;
  line-height: 1;
  padding: 0 5px;
  position: absolute;
  right: 3px;
  top: 5px;
  width: 24px;

  svg {
    height: auto;
    width: 100%;
  }
`

const Banner = ({ id, children, cookies, pageId, hideOnPages, width }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(true)
  const intl = useIntl()

  useEffect(() => {
    if (
      !cookies.get(`${id}_banner_closed`) &&
      (hideOnPages ? hideOnPages.indexOf(pageId) === -1 : true)
    ) {
      setIsOpen(true)
      setIsHidden(false)
    }
  }, [id, cookies, hideOnPages, pageId])

  const closeBanner = () => {
    cookies.set(`${id}_banner_closed`, true, {
      expires: daysFromNow(30),
      path: "/",
      httpOnly: false,
    })

    setIsOpen(false)
    setTimeout(() => {
      setIsHidden(true)
    }, 300)

    debug && console.log(`Set cookie "${id}_banner_closed" to true`)
  }

  return !isHidden ? (
    <Flyout
      aria-label={intl.formatMessage({ id: "accessibility.regions.banner" })}
      aria-live="polite"
      $isOpen={isOpen}
      $width={width}
      data-testid="banner"
      role="complementary"
    >
      {children}
      <Close
        aria-label={intl.formatMessage({ id: "close" })}
        onClick={closeBanner}
        type="button"
      >
        <CloseSVG aria-hidden="true" />
      </Close>
    </Flyout>
  ) : (
    ""
  )
}

Banner.propTypes = {
  hideOnPages: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  width: PropTypes.number,
}

export default withCookies(Banner)
